/**
 * Enhanced Centralized Application Store
 * Implements Smart Data-Flow Architecture principles
 * Manages all application state with standardized patterns
 */

import authService from '../services/AuthService';
import { rootReducer, createEnhancedStore } from './reducers';
import { staffActions, corporateActions, driverActions, uiActions } from './actions';

class AppStore {
  constructor() {
    this.state = {
      // User authentication state
      user: {
        email: null,
        roleId: null,
        corpId: null,
        departmentId: null,
        corporateAddress: null,
        isAuthenticated: false,
      },
      
      // Entity data
      corporates: [],
      departments: [],
      staff: [],
      routes: [],
      drivers: [],
      trips: [],
      bookings: [],
      settings: {},
      
      // System state
      loading: {},
      errors: {},
      notifications: [],
      
      // UI state
      filters: {},
      selectedItems: {},
      pagination: {},
    };
    
    this.listeners = new Set();
    this.loadInitialState();
    
    // Smart Data-Flow: Add standardized action dispatch
    this.actions = {
      staff: staffActions,
      corporates: corporateActions,
      drivers: driverActions,
      ui: uiActions
    };
  }

  /**
   * Load initial state from AuthService
   */
  loadInitialState() {
    try {
      // Use AuthService instead of direct localStorage access
      const authUser = authService.getUser();
      const isAuthenticated = authService.isAuthenticated();

      const user = {
        email: authUser?.emailID || null,
        roleId: authUser?.roleID || null,
        corpId: authUser?.corporateID || null,
        departmentId: authUser?.departmentID || null,
        corporateAddress: authUser?.corporateAddress || null,
        isAuthenticated,
      };

      this.state.user = user;

      // Initialize role-based permissions using AuthService
      this.state.permissions = {
        isSuperAdmin: authService.hasRole("1"),
        canManageCorporates: authService.hasRole("1"),
        canManageStaff: authService.hasRole("1") || authService.hasRole("2"),
        canViewReports: authService.hasRole("1") || authService.hasRole("2") || authService.hasRole("3"),
      };
    } catch (error) {
      console.error('Error loading initial state:', error);
    }
  }

  /**
   * Get current state
   */
  getState() {
    return this.state;
  }

  /**
   * Smart Data-Flow: Standardized action dispatch
   * Replaces inconsistent state updates across components
   */
  dispatch(action) {
    if (!action || !action.type) {
      return;
    }
    
    const previousState = this.state;
    this.state = rootReducer(this.state, action);
    
    // Only notify listeners if state actually changed
    if (previousState !== this.state) {
      this.notifyListeners();
    
    }
    
    return this.state;
  }

  /**
   * Standardized state update method
   */
  updateState(path, value) {
    const keys = path.split('.');
    let current = this.state;
    
    // Navigate to the parent of the target property
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    
    // Set the final value
    current[keys[keys.length - 1]] = value;
    
    // Notify all listeners
    this.notifyListeners();
  }

  /**
   * Update multiple state paths atomically
   */
  updateMultiple(updates) {
    Object.entries(updates).forEach(([path, value]) => {
      const keys = path.split('.');
      let current = this.state;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
    });
    
    this.notifyListeners();
  }

  /**
   * Unified loading state management
   */
  setLoading(operation, isLoading) {
    this.updateState(`loading.${operation}`, isLoading);
  }

  /**
   * Unified error handling
   */
  setError(operation, error) {
    this.updateState(`errors.${operation}`, error);
    
    // Clear error after 10 seconds
    if (error) {
      setTimeout(() => {
        this.updateState(`errors.${operation}`, null);
      }, 10000);
    }
  }

  /**
   * Add notification
   */
  addNotification(notification) {
    const id = Date.now();
    const newNotification = {
      id,
      timestamp: new Date().toISOString(),
      ...notification
    };
    
    this.updateState('notifications', [...this.state.notifications, newNotification]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      this.removeNotification(id);
    }, 5000);
    
    return id;
  }

  /**
   * Remove notification
   */
  removeNotification(id) {
    const filtered = this.state.notifications.filter(n => n.id !== id);
    this.updateState('notifications', filtered);
  }

  /**
   * Update user authentication state
   */
  setUser(userData) {
    this.updateState('user', { ...this.state.user, ...userData });

    // Note: AuthService handles its own storage management
    // This method is primarily for updating the store state
    // Actual authentication should go through AuthService.login()
  }

  /**
   * Clear user state (logout)
   */
  clearUser() {
    this.updateState('user', {
      email: null,
      roleId: null,
      corpId: null,
      departmentId: null,
      corporateAddress: null,
      isAuthenticated: false,
    });

    // Use AuthService to clear authentication data
    authService.clearAuth();
  }

  /**
   * Entity-specific update methods
   */
  setCorporates(corporates) {
    this.updateState('corporates', corporates);
  }

  addCorporate(corporate) {
    this.updateState('corporates', [...this.state.corporates, corporate]);
  }

  updateCorporate(id, updates) {
    const updated = this.state.corporates.map(corp => 
      corp.id === id ? { ...corp, ...updates } : corp
    );
    this.updateState('corporates', updated);
  }

  removeCorporate(id) {
    const filtered = this.state.corporates.filter(corp => corp.id !== id);
    this.updateState('corporates', filtered);
  }

  // Similar methods for other entities
  setStaff(staff) {
    this.updateState('staff', staff);
  }

  addStaff(staffMember) {
    this.updateState('staff', [...this.state.staff, staffMember]);
  }

  updateStaff(id, updates) {
    const updated = this.state.staff.map(member => 
      member.id === id ? { ...member, ...updates } : member
    );
    this.updateState('staff', updated);
  }

  removeStaff(id) {
    const filtered = this.state.staff.filter(member => member.id !== id);
    this.updateState('staff', filtered);
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of state changes
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.state);
      } catch (error) {
        console.error('Error in state listener:', error);
      }
    });
  }

  /**
   * Reset entire store
   */
  reset() {
    this.state = {
      user: { email: null, roleId: null, corpId: null, departmentId: null, corporateAddress: null, isAuthenticated: false },
      corporates: [],
      departments: [],
      staff: [],
      routes: [],
      drivers: [],
      trips: [],
      bookings: [],
      settings: {},
      loading: {},
      errors: {},
      notifications: [],
      filters: {},
      selectedItems: {},
      pagination: {},
    };
    this.notifyListeners();
  }

  /**
   * Get filtered data
   */
  getFilteredData(entityName, filters = {}) {
    const data = this.state[entityName] || [];
    
    if (Object.keys(filters).length === 0) {
      return data;
    }
    
    return data.filter(item => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        return item[key]?.toString().toLowerCase().includes(value.toString().toLowerCase());
      });
    });
  }

  /**
   * Smart Data-Flow: Enhanced entity data management
   */
  setEntityData(entityName, data) {
    const action = this.actions[entityName]?.set || 
                   { type: `${entityName.toUpperCase()}/SET`, payload: data };
    return this.dispatch(action);
  }

  addEntity(entityName, entity) {
    const action = this.actions[entityName]?.add || 
                   { type: `${entityName.toUpperCase()}/ADD`, payload: entity };
    return this.dispatch(action);
  }

  updateEntity(entityName, id, updates) {
    const action = this.actions[entityName]?.update || 
                   { type: `${entityName.toUpperCase()}/UPDATE`, payload: { id, updates } };
    return this.dispatch(action);
  }

  removeEntity(entityName, id) {
    const action = this.actions[entityName]?.remove || 
                   { type: `${entityName.toUpperCase()}/REMOVE`, payload: id };
    return this.dispatch(action);
  }

  setEntityLoading(entityName, loading) {
    const action = this.actions[entityName]?.setLoading || 
                   { type: `${entityName.toUpperCase()}/SET_LOADING`, payload: loading };
    return this.dispatch(action);
  }

  setEntityError(entityName, error) {
    const action = this.actions[entityName]?.setError || 
                   { type: `${entityName.toUpperCase()}/SET_ERROR`, payload: error };
    return this.dispatch(action);
  }
}

// Export enhanced singleton instance with Smart Data-Flow capabilities
const baseStore = new AppStore();
export const appStore = createEnhancedStore(baseStore);

// Smart Data-Flow debugging in development
if (process.env.NODE_ENV === 'development') {
  window.appStore = appStore;
  window.logStoreState = () => console.log('[APP_STORE] Current State:', appStore.getState());
  
  }

export default appStore;